import React, { ChangeEvent, useEffect, useState } from 'react';

import { Box, MenuItem, Typography } from '@material-ui/core';
import dayjs from 'dayjs';
import capitalize from 'lodash/capitalize';

import { SettingsCard, SettingsOptionList, SettingsOptionRow } from '@vyce/core/src/components/SettingsCard';
import { AppSelect } from '@vyce/core/src/components/inputs';
import { TimePeriodSettings } from '@vyce/core/src/types';
import { formatDate } from '@vyce/core/src/utils/dates';

import { timelogDays, TimeLogsPeriods } from '../config';
import { useStyles } from '../styles';

const PERIOD_LABELS: Record<TimeLogsPeriods, string> = {
  [TimeLogsPeriods.WEEKLY]: 'Weekly',
  [TimeLogsPeriods.BI_WEEKLY]: 'Fortnightly',
  [TimeLogsPeriods.MONTHLY]: 'Monthly',
};

interface Props {
  endDay: string;
  settings: TimePeriodSettings;
  expanded: boolean;
  onExpandedChange: (expanded: boolean) => void;
  onPeriodChange: (settings: TimePeriodSettings) => Promise<void>;
}

export const TimeLogsReporting: React.FC<Props> = ({
  endDay,
  settings,
  expanded,
  onExpandedChange,
  onPeriodChange,
}) => {
  const classes = useStyles();
  const [startDayOptions, setStartDayOptions] = useState<string[]>([]);

  const getBiWeeklyDays = (weekday: string): string[] => {
    const targetDayIndex = timelogDays.indexOf(weekday.toLowerCase());
    if (targetDayIndex === -1) return [];

    const today = new Date();
    today.setHours(0);
    const todayDayIndex = today.getDay();
    let daysToAdd = targetDayIndex - todayDayIndex + 1;
    if (daysToAdd < 0) daysToAdd += 7;

    const nextWeekdayDate = new Date(today);
    nextWeekdayDate.setDate(today.getDate() + daysToAdd);

    const weekAfterDate = new Date(nextWeekdayDate);
    weekAfterDate.setDate(nextWeekdayDate.getDate() + 7);

    return [formatDate(nextWeekdayDate), formatDate(weekAfterDate)];
  };

  const handlePeriodSelect = (type: TimeLogsPeriods) => () => {
    if (settings.period_type === type) return;

    const isMonthly = type === TimeLogsPeriods.MONTHLY;
    const isWeekly = type === TimeLogsPeriods.WEEKLY;
    const weekdayStart = settings.weekday_start || 'monday';
    let periodStartDate;
    if (type === TimeLogsPeriods.BI_WEEKLY) {
      const options = getBiWeeklyDays(weekdayStart);
      setStartDayOptions(options);
      periodStartDate = settings.period_start_date || options[0];
    }

    onPeriodChange({
      ...settings,
      period_type: type,
      weekday_start: !isMonthly ? weekdayStart : null,
      period_start_date: !isMonthly && !isWeekly ? periodStartDate : null,
    });
  };

  const onWeekdayChange = (day: string) => {
    let periodStartDate;
    if (settings.period_type === TimeLogsPeriods.BI_WEEKLY) {
      const options = getBiWeeklyDays(day);
      setStartDayOptions(options);
      periodStartDate = options[0];
    }
    onPeriodChange({
      ...settings,
      weekday_start: day,
      period_start_date: periodStartDate,
    });
  };

  const onPeriodStartDayChange = (e: ChangeEvent<any>) => {
    onPeriodChange({
      ...settings,
      period_type: TimeLogsPeriods.BI_WEEKLY,
      period_start_date: e.target.value,
    });
  };

  useEffect(() => {
    if (settings.weekday_start && settings.period_type === TimeLogsPeriods.BI_WEEKLY) {
      setStartDayOptions(getBiWeeklyDays(settings.weekday_start));
    }
  }, [settings]);

  const weekStartPicker = (
    <Box display="flex" alignItems="center" gridGap={10} flexWrap="wrap">
      <Typography className={classes.detailText}>Weeks start on</Typography>

      <AppSelect
        value={settings.weekday_start ?? 'monday'}
        onChange={e => onWeekdayChange(e.target.value as string)}
        className={classes.selectRoot}
      >
        {timelogDays.map(day => (
          <MenuItem key={day} value={day}>
            <span>{capitalize(day)}</span>
          </MenuItem>
        ))}
      </AppSelect>
    </Box>
  );

  return (
    <SettingsCard
      title="Reporting period"
      description="How daily time logs are grouped when you report or export them — a week, a fortnight or a calendar month."
      status={PERIOD_LABELS[settings.period_type as TimeLogsPeriods]}
      expanded={expanded}
      onExpandedChange={onExpandedChange}
    >
      <SettingsOptionList>
        <SettingsOptionRow
          label={PERIOD_LABELS[TimeLogsPeriods.WEEKLY]}
          selected={settings.period_type === TimeLogsPeriods.WEEKLY}
          onSelect={handlePeriodSelect(TimeLogsPeriods.WEEKLY)}
        >
          <Typography className={classes.detailText}>
            Each report covers midnight {capitalize(settings.weekday_start ?? 'monday')} through 23:59{' '}
            {capitalize(endDay)}.
          </Typography>

          {weekStartPicker}
        </SettingsOptionRow>

        <SettingsOptionRow
          label={PERIOD_LABELS[TimeLogsPeriods.BI_WEEKLY]}
          selected={settings.period_type === TimeLogsPeriods.BI_WEEKLY}
          onSelect={handlePeriodSelect(TimeLogsPeriods.BI_WEEKLY)}
        >
          <Typography className={classes.detailText}>
            Each report covers two weeks
            {settings.period_start_date
              ? `, starting ${dayjs(settings.period_start_date).format('dddd D MMM')}`
              : ''}
            .
          </Typography>

          {weekStartPicker}

          <Box display="flex" alignItems="center" gridGap={10} flexWrap="wrap">
            <Typography className={classes.detailText}>The next period starts</Typography>

            <AppSelect
              value={settings.period_start_date ?? ''}
              onChange={onPeriodStartDayChange}
              className={classes.selectRoot}
            >
              {startDayOptions.map(day => (
                <MenuItem key={day} value={day}>
                  <span>{dayjs(day).format('dddd DD.MM')}</span>
                </MenuItem>
              ))}
            </AppSelect>
          </Box>
        </SettingsOptionRow>

        <SettingsOptionRow
          label={PERIOD_LABELS[TimeLogsPeriods.MONTHLY]}
          selected={settings.period_type === TimeLogsPeriods.MONTHLY}
          onSelect={handlePeriodSelect(TimeLogsPeriods.MONTHLY)}
        >
          <Typography className={classes.detailText}>
            Each report covers the 1st of the month through 23:59 on the last day.
          </Typography>
        </SettingsOptionRow>
      </SettingsOptionList>
    </SettingsCard>
  );
};
